/**
 * ZEN ERP — Baskı Önizleme Modal ve Baskı Dialog Bileşenleri
 * App.js'den çıkarıldı — tüm bağımlılıklar yukarıdan import edildi.
 */
import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Printer, ChevronDown, X, FileDown, Mail, Send,
  Radio, Barcode, FileText, Factory, Receipt, Tag, Share2, PencilRuler, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

import api from "../api";
import { ono, shareUrl } from "../appUtils";
import { UICtx, NavigationCtx } from "../appContexts";
import { jspm } from "../lib/jspm";
import { buildLabelZPL } from "../pages/SettingsPage";
import {
  buildWorkOrderDoc, buildWholesalerDoc, buildLabelDoc,
  loadLogoDataUrl, fetchRates,
  generateQuotePDF, generateWorkOrderPDF, generateWholesalerPDF, generateLabelPDF,
  generateTulFonSchemaPDF, buildTulFonSchemaDoc,
  smartShareFile, PREVIEW_DOC_TYPES, smartSharePDF, printViaIframe,
} from "../utils/pdfUtils";
import { buildTeklifPrintHTML } from "../quotes";
import { hasTulFonItems } from "../utils/tulFonSheetModel";
import { PdfDocumentPreview } from "./PdfDocumentPreview";
import { PdfPrintPreview, PreviewToolbar } from "./PdfPrintPreview";
import { browserPrintHtml } from "../utils/browserPrintHtml";

// ── Baskı Önizleme Modal ──────────────────────────────────────────────────────
export function PrintPreviewModal({ order, onClose, initialDocType = "teklif", refType = "order" }) {
  const { tr, lang } = useContext(UICtx);
  const { setTab } = useContext(NavigationCtx);
  const [docType, setDocType] = useState(initialDocType);
  const [readyDoc, setReadyDoc] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [retry, setRetry] = useState(0);
  const [html, setHtml] = useState("");
  const [teklifH, setTeklifH] = useState(0);             // teklif iframe yüksekliği (px)
  const [teklifZoom, setTeklifZoom] = useState(null);     // null → sayfaya sığdır
  const [teklifFit, setTeklifFit] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const iframeRef = useRef(null);
  const teklifAreaRef = useRef(null);
  const teklifScale = teklifZoom ?? teklifFit;

  const buildDoc = async (type) => {
    if (type === "tulfon") return { doc: await buildTulFonSchemaDoc(order), filename: `ZEN-Terzi-Foyu-${order.orderNo || order.quoteNo || order.id}.pdf`, text: `ZEN Terzi Föyü — ${order.orderNo || order.quoteNo} · ${order.customerName}` };
    if (type === "imalat") return { doc: await buildWorkOrderDoc(order), filename: `ZEN-ImalatFisi-${ono(order)}.pdf`, text: `ZEN İmalat Fişi — ${ono(order)} · ${order.customerName}` };
    if (type === "toptanci") return { doc: await buildWholesalerDoc(order), filename: `ZEN-Toptanci-${ono(order)}.pdf`, text: `ZEN Toptancı Sipariş Fişi — ${ono(order)} · ${order.customerName}` };
    if (type === "etiket") return { doc: await buildLabelDoc(order, { w: 70, h: 40 }), filename: `ZEN-Etiket-${ono(order)}.pdf`, text: `ZEN Etiket — ${ono(order)}` };
    return null;
  };

  /* Siparişi QuotePrintModal motorunun beklediği teklif objesine dönüştür */
  const orderAsQuote = React.useCallback((rates) => ({
    quoteNo:        order.quoteNo || order.orderNo,
    brand:          order.brand  || "zen",
    items:          order.items  || [],
    subtotal:       order.subtotal  || 0,
    total:          order.total     || 0,
    vatBreakdown:   order.vatBreakdown || [],
    currency:       order.currency || "TL",
    customerName:   order.customerName || "",
    customerId:     order.customerId,
    notes:          order.quoteNotes || order.notes || "",
    projectName:    order.projectName || "",
    bankAccountId:  order.bankAccountId,
    deliveryAddress: order.deliveryAddress || "",
    discountAmount: order.discountAmount || 0,
    discountType:   order.discountType  || "",
    validDays:      order.validDays || 15,
    revisionNo:     order.revisionNo || 1,
    rates,
  }), [order]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setHtml(""); setReadyDoc(null); setPreviewError(""); setTeklifH(0); setTeklifZoom(null);
      try {
      if (docType === "teklif") {
        const rates = await fetchRates();
        if (!cancelled) setHtml(buildTeklifPrintHTML(orderAsQuote(rates), lang));
      } else {
        const d = await buildDoc(docType);
        if (!cancelled && d) setReadyDoc(d);   // önizleme pdf.js ile doc üzerinden yapılır
      }
      } catch (e) { if (!cancelled) setPreviewError(e.message || "Belge oluşturulamadı"); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, docType, lang, retry]);

  // Teklif (HTML) önizlemesi: VARSAYILAN ölçek GENİŞLİĞE göredir (29.10 — uzun
  // föyler "sayfaya sığdır" ile %33'e kadar küçülüyordu, okunamıyordu). Belge
  // genişliği doldurur; uzunluk dikey kaydırma ile okunur.
  useEffect(() => {
    const area = teklifAreaRef.current;
    if (!area || docType !== "teklif" || !teklifH) return;
    const compute = () => {
      const availH = area.clientHeight - 16;
      const availW = area.clientWidth - 16;
      if (availH <= 0 || availW <= 0) return;
      setTeklifFit(Math.max(0.3, Math.min(availW / 820, 1.5)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(area);
    return () => ro.disconnect();
  }, [teklifH, docType]);

  useEffect(() => {
    const close = event => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose]);

  if (!order) return null;

  const doSilentPrint = async () => {
    setMenuOpen(false);
    if (!jspm.isConnected()) {
      toast.error("JSPM Client bağlı değil — Ayarlar → Yazıcılar → Yazıcı Bağlantısı'ndan kontrol edin");
      return;
    }
    const roleKey = docType === "etiket" ? "label" : "document";
    try {
      const { data } = await api.get("/settings", { params: { category: "jspm_printers" } });
      const cfg = {};
      (data || []).forEach((r) => { cfg[r.key] = r.value; });
      const printer = cfg[roleKey];
      if (!printer) {
        toast.error(`${roleKey === "label" ? "Etiket" : "Belge"} yazıcısı seçilmemiş — Ayarlar → Yazıcılar → JSPM'den seçin`);
        return;
      }
      let blob, filename;
      if (docType === "teklif") {
        // Yeni HTML motoru → sunucuda PDF'e dönüştür
        filename = `Teklif-${order.quoteNo || order.orderNo || "X"}.pdf`;
        try {
          const { data } = await api.post("/share/html-pdf", { html, filename });
          const resp = await fetch(shareUrl(`${data.path}/file`));
          blob = await resp.blob();
        } catch {
          toast.error("PDF oluşturulamadı — sessiz yazdırma iptal edildi"); return;
        }
      } else {
        const d = readyDoc || await buildDoc(docType);
        blob = d.doc.output("blob");
        filename = d.filename;
      }
      await jspm.silentPrintPDF(printer, blob, filename);
      toast.success(`Sessizce yazdırıldı → ${printer}`);
    } catch (e) {
      toast.error("Sessiz yazdırma hatası: " + (e.message || "Bilinmiyor"));
    }
  };

  const doSilentPrintZPL = async () => {
    setMenuOpen(false);
    if (!jspm.isConnected()) {
      toast.error("JSPM Client bağlı değil");
      return;
    }
    try {
      const { data } = await api.get("/settings", { params: { category: "jspm_printers" } });
      const cfg = {};
      (data || []).forEach((r) => { cfg[r.key] = r.value; });
      const printer = cfg["zpl_label"];
      if (!printer) {
        toast.error("Zebra (ZPL) yazıcı seçilmemiş — Ayarlar → Yazıcılar → JSPM'den seçin");
        return;
      }
      const zpl = await buildLabelZPL(order);
      await jspm.silentPrintZPL(printer, zpl);
      toast.success(`ZPL etiket gönderildi → ${printer}`);
    } catch (e) {
      toast.error("ZPL yazdırma hatası: " + (e.message || "Bilinmiyor"));
    }
  };

  const doPrint = async () => {
    setMenuOpen(false);
    const toastId = "zen-print";
    toast.loading("Yazdırma hazırlanıyor…", { id: toastId, duration: 8000 });
    try {
      if (docType === "teklif") {
        printViaIframe(browserPrintHtml(html), false);
        toast.dismiss(toastId);
      } else {
        const d = readyDoc || await buildDoc(docType);
        const blob = d.doc.output("blob");
        const url = URL.createObjectURL(blob);
        printViaIframe(url, true, url);
        toast.dismiss(toastId);
      }
    } catch (e) {
      toast.dismiss(toastId);
      toast.error("Yazdırma hatası: " + (e.message || "Bilinmiyor"));
    }
  };

  const doSavePdf = async () => {
    setMenuOpen(false);
    if (docType === "teklif") {
      try {
        const filename = `Teklif-${order.quoteNo || order.orderNo || "X"}.pdf`;
        const { data } = await api.post("/share/html-pdf", { html, filename });
        window.open(shareUrl(`${data.path}/file`), "_blank");
        toast.success("PDF indirildi");
      } catch { toast.error("PDF oluşturulamadı"); }
    } else {
      try {
        const d = readyDoc || await buildDoc(docType);
        d.doc.save(d.filename);
        toast.success("PDF indirildi");
      } catch (e) { toast.error(e.message || "PDF oluşturulamadı"); }
    }
  };

  const doShare = async (via) => {
    setMenuOpen(false);
    if (docType === "teklif") {
      try {
        const filename = `Teklif-${order.quoteNo || order.orderNo || "X"}.pdf`;
        const { data } = await api.post("/share/html-pdf", { html, filename, refType, refId: order.id });
        const link = shareUrl(data.path);
        if (via === "whatsapp") {
          const phone = String(order.customerPhone || "").replace(/[^0-9]/g, "");
          const intl = phone ? (phone.startsWith("90") ? phone : phone.replace(/^0+/, "90")) : "";
          const msg = `Sayın ${order.customerName || "Müşteri"},\n\nSiparişinize ait teklifi aşağıdan indirebilirsiniz:\n${link}`;
          window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
        } else {
          window.open(link, "_blank");
        }
      } catch { toast.error("Paylaşım linki oluşturulamadı"); }
    } else {
      try {
        const d = readyDoc || await buildDoc(docType);
        await smartShareFile(d.doc, d.filename, via, d.text, "", { refType, refId: order.id });
      } catch (e) { toast.error(e.message || "Paylaşım hazırlanamadı"); }
    }
  };

  const menuItems = [
    { icon: Radio, label: "Sessiz Yazdır (JSPM)", desc: "Seçili yazıcıya doğrudan gönder — dialog açılmaz", onClick: doSilentPrint, testid: "preview-silent-print", primary: true },
    ...(docType === "etiket" ? [
      { icon: Barcode, label: "Zebra ZPL Yazdır", desc: "Zebra/Godex etiket yazıcısına ham ZPL komutu gönder — barkod çok net", onClick: doSilentPrintZPL, testid: "preview-silent-zpl" },
    ] : []),
    { icon: Printer, label: "Yazdır / Yazıcı Seç", desc: "Yazıcı seçimi veya 'PDF olarak kaydet'", onClick: doPrint, testid: "preview-print" },
    { icon: FileDown, label: "PDF Olarak Kaydet", desc: "Görüntülenen belgeyi indir", onClick: doSavePdf, testid: "preview-save-pdf" },
    { icon: Mail, label: "E-Posta ile Gönder", desc: "PDF + özet e-posta taslağı", onClick: () => doShare("email"), testid: "preview-email" },
    { icon: Send, label: "WhatsApp ile Gönder", desc: "PDF + özet mesajı", onClick: () => doShare("whatsapp"), testid: "preview-whatsapp" },
  ];
  const activeDoc = PREVIEW_DOC_TYPES.find(d => d.key === docType);
  const docLabel = (d) => (lang === "en" ? d.en : d.label);
  const activeDocLabel = activeDoc ? docLabel(activeDoc) : "";

  return (
    <div className="fixed inset-0 z-[200] zen-print-center flex flex-col overflow-hidden" data-testid="print-preview-modal" role="dialog" aria-modal="true" aria-labelledby="print-preview-title">
      {/* TAM SAYFA yazdırma çalışma alanı — Etiket Bas sayfasıyla aynı yerleşim */}
      <div className="flex flex-col flex-1 min-h-0 w-full">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2 border-b border-white/15">
          <div className="flex items-center gap-3 min-w-0">
            {/* MİNİK GERİ OKU — sipariş LİSTESİNE döner (her sayfada var, X'e gerek yok) */}
            <button
              data-testid="preview-back-to-order"
              title={refType === "quote" ? "Teklif listesine dön" : "Sipariş listesine dön"}
              onClick={() => {
                setTab(refType === "quote" ? "quotes" : "orders");
                onClose();
              }}
              className="zen-evrak-btn shrink-0">
              <ArrowLeft className="w-4 h-4" /> {refType === "quote" ? "Tekliflere Dön" : "Siparişlere Dön"}
            </button>
            <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
              <Printer className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 id="print-preview-title" data-testid="preview-title" className="text-lg sm:text-xl font-extrabold tracking-tight break-words">{activeDocLabel} <span className="zen-mono text-[#FF6B5E]">· {order.orderNo || order.quoteNo || ono(order)}</span></h3>
              <p data-testid="preview-customer" className="text-xs text-white/60 break-words">{order.customerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              <button data-testid="preview-actions-btn" onClick={() => setMenuOpen(!menuOpen)} className="zen-btn-primary !text-xs">
                <Printer className="w-4 h-4" /> {tr("Yazdır / Gönder")} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 w-72 zen-panel !rounded-lg shadow-xl z-20 overflow-hidden" data-testid="preview-actions-menu">
                  {menuItems.map((m) => (
                    <button key={m.testid} data-testid={m.testid} onClick={m.onClick} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-100 dark:hover:bg-[#22304A] transition text-left">
                      <m.icon className="w-4 h-4 text-[#E5322A] shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-xs font-bold">{m.label}</span>
                        <span className="block text-[10px] text-slate-500">{m.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 p-2 border-b border-white/15 dark:border-white/15" data-testid="preview-document-tabs">
          <span className="text-[10px] font-bold text-slate-400 dark:text-white/50 uppercase tracking-wider mr-1 select-none">Evrak türü →</span>
          {PREVIEW_DOC_TYPES.filter(d => d.key !== "tulfon" || hasTulFonItems(order)).map(d => <button key={d.key} data-testid={`preview-doc-${d.key}`} onClick={() => setDocType(d.key)} aria-pressed={docType === d.key}
            className={`zen-evrak-btn ${docType === d.key ? "is-active" : ""}`}><d.icon className="w-3.5 h-3.5" />{docLabel(d)}</button>)}
          {/* Küçük Etiket — EtiketBas sayfasına yönlendir */}
          <button
            data-testid="preview-doc-kucuk-etiket"
            title="EtiketBas sayfasında bu siparişin ürün etiketlerini yazdır"
            onClick={() => {
              try { localStorage.setItem("zen_label_preselect_order", order.id); } catch {}
              setTab("label_print");
              onClose();
            }}
            className="zen-evrak-btn"
          >
            <Tag className="w-3.5 h-3.5" />
            Küçük Etiket Bas
          </button>
        </div>
        {docType === "tulfon" && <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <span data-testid="tailor-document-status" className="text-xs font-semibold text-sky-700 dark:text-sky-300">{refType === "quote" ? "Teklif taslağı · Üretim onayı değildir" : "Fiyatsız üretim föyü"}</span>
          <div className="flex gap-2">
            <button data-testid="tailor-download-pdf" disabled={!readyDoc} onClick={doSavePdf} className="zen-btn-primary !text-xs disabled:opacity-40"><FileDown className="w-4 h-4" /> PDF İndir</button>
            <button data-testid="tailor-print-pdf" disabled={!readyDoc} onClick={doPrint} className="zen-btn-ghost !text-xs disabled:opacity-40"><Printer className="w-4 h-4" /> Yazdır</button>
          </div>
        </div>}
        <div className="flex-1 min-h-0 bg-black/20 dark:bg-black/25 p-2 sm:p-3 overflow-auto" data-testid="preview-document-body" onClick={() => setMenuOpen(false)}>
          {previewError && <div role="alert" data-testid="preview-build-error" className="p-4 text-sm text-red-700 bg-red-50 break-words">{previewError}<button data-testid="preview-retry" onClick={() => setRetry(n => n + 1)} className="block underline mt-2">Yeniden dene</button></div>}
          {docType === "tulfon" && readyDoc && <PdfDocumentPreview doc={readyDoc.doc} />}
          {docType === "teklif" && html && (
            /* Teklif/Fatura — A4 ortalanmış, diğer evraklarla AYNI çerçeve ve Türkçe araç çubuğu */
            <div className="mx-auto w-full h-full flex flex-col" style={{ maxWidth: "1500px" }}>
              <div className="zen-panel p-2 sm:p-3 flex-1 min-h-0 flex flex-col">
                <div data-testid="preview-filename" className="text-[11px] font-semibold text-slate-500 dark:text-white/60 mb-1 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> {`Teklif-${order.quoteNo || order.orderNo || "X"}.pdf`}
                </div>
                <PreviewToolbar
                  zoomPct={Math.round(teklifScale * 100)}
                  fitActive={teklifZoom === null}
                  fitLabel="Genişliğe sığdır"
                  onZoomIn={() => setTeklifZoom(Math.min(2.5, (teklifZoom ?? teklifFit) * 1.2))}
                  onZoomOut={() => setTeklifZoom(Math.max(0.3, (teklifZoom ?? teklifFit) / 1.2))}
                  onFit={() => setTeklifZoom(null)}
                  onDownload={doSavePdf}
                  onPrint={doPrint}
                />
                <div ref={teklifAreaRef} className="mt-2 flex flex-col flex-1 min-h-[420px] overflow-auto rounded-xl bg-black/10 dark:bg-black/20 p-2">
                  <div className="m-auto" style={{ width: "820px", height: teklifH ? teklifH * teklifScale : undefined }}>
                    <iframe ref={iframeRef} title="Belge Önizleme" srcDoc={browserPrintHtml(html)}
                      onLoad={(e) => {
                        // Belge yüksekliğine göre boy ver (uzun evraklar kırpılmasın)
                        try {
                          const doc = e.target.contentDocument;
                          const h = doc ? doc.documentElement.scrollHeight : 0;
                          if (h > 100) { e.target.style.height = h + "px"; setTeklifH(h); }
                        } catch { /* erişilemezse sabit yükseklik kalır */ }
                      }}
                      className="w-full rounded-lg shadow-2xl border-0"
                      style={{ background: "#ffffff", colorScheme: "light", height: teklifH ? `${teklifH}px` : "1160px", transform: `scale(${teklifScale})`, transformOrigin: "top center" }}
                      data-testid="preview-iframe" />
                  </div>
                </div>
              </div>
            </div>
          )}
          {docType !== "teklif" && docType !== "tulfon" && readyDoc && (
            /* PDF evraklar — Chrome'un koyu PDF kutusu yerine kendi pdf.js önizlememiz:
               beyaz sayfa degrade zemin üzerinde ortalanır + Türkçe araç çubuğu */
            <div className="mx-auto w-full h-full flex flex-col" style={{ maxWidth: "1500px" }}>
              <div className="zen-panel p-2 sm:p-3 flex-1 min-h-0 flex flex-col">
                <div data-testid="preview-filename" className="text-[11px] font-semibold text-slate-500 dark:text-white/60 mb-1 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> {readyDoc.filename}
                </div>
                <PdfPrintPreview doc={readyDoc.doc} onDownload={doSavePdf} onPrint={doPrint} />
              </div>
            </div>
          )}
          {!previewError && ((docType === "teklif" && !html) || (docType !== "teklif" && !readyDoc)) && (
            <div data-testid="preview-loading" className="h-[60vh] flex items-center justify-center text-sm text-slate-500">{tr("Önizleme hazırlanıyor…")}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Baskı Dialog ──────────────────────────────────────────────────────────────
export function PrintDialog({ order, onClose }) {
  const [labelW, setLabelW] = useState(70);
  const [labelH, setLabelH] = useState(40);
  const [autoPrintLabel, setAutoPrintLabel] = useState(true);
  const [showItemLabels, setShowItemLabels] = useState(false);
  if (!order) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-lg p-6 zen-anim-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h3 className="text-lg font-bold">Çıktı Seç - {ono(order)}</h3>
            <p className="text-xs text-slate-500">{order.customerName}</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-2">
          <button data-testid="print-quote" onClick={() => generateQuotePDF(order)} className="w-full flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-[#22304A] hover:bg-slate-50 dark:hover:bg-[#0C1220] transition">
            <div className="w-10 h-10 rounded-lg bg-[#E5322A]/10 text-[#E5322A] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
            <div className="text-left flex-1"><div className="font-bold text-sm">Teklif / Fatura Çıktısı</div><div className="text-xs text-slate-500">Müşteriye gönderilecek fiyatlı belge (A4)</div></div>
          </button>
          <button data-testid="print-work" onClick={() => generateWorkOrderPDF(order)} className="w-full flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-[#22304A] hover:bg-slate-50 dark:hover:bg-[#0C1220] transition">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center"><Factory className="w-5 h-5" /></div>
            <div className="text-left flex-1"><div className="font-bold text-sm">İmalat Fişi (Fiyatsız)</div><div className="text-xs text-slate-500">Üretim için kesim ölçüleri (A4)</div></div>
          </button>
          {(order.items || []).some(it => it.groupSlug === "tul_fon_perde" && !it.isAccessory) && (
            <button data-testid="print-tulfon-schema" onClick={() => generateTulFonSchemaPDF(order)} className="w-full flex items-center gap-3 p-4 rounded-lg border border-sky-200 dark:border-sky-900/40 bg-sky-500/5 hover:bg-sky-500/10 transition">
              <div className="w-10 h-10 rounded-lg bg-sky-500/15 text-sky-600 flex items-center justify-center"><PencilRuler className="w-5 h-5" /></div>
              <div className="text-left flex-1"><div className="font-bold text-sm text-sky-700 dark:text-sky-400">Tül/Fon Şemalı İmalat Föyü</div><div className="text-xs text-slate-500">Kanat görsel şeması + mühendislik ölçüleri (A4)</div></div>
            </button>
          )}
          <button data-testid="print-wholesaler" onClick={() => generateWholesalerPDF(order)} className="w-full flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-[#22304A] hover:bg-slate-50 dark:hover:bg-[#0C1220] transition">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center"><Receipt className="w-5 h-5" /></div>
            <div className="text-left flex-1"><div className="font-bold text-sm">Toptancıya Detaylı Sipariş</div><div className="text-xs text-slate-500">Kumaş, renk, RAL, motor bilgileri (A4)</div></div>
          </button>
          <div className="p-4 rounded-lg border border-slate-200 dark:border-[#22304A]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 text-sky-500 flex items-center justify-center"><Printer className="w-5 h-5" /></div>
              <div className="flex-1">
                <div className="font-bold text-sm">Etiket Çıktısı (Barkodlu)</div>
                <div className="text-[10px] text-slate-500">Boyut ayarlanabilir · Code128 barkodlu · Termal etiket yazıcı uyumlu</div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 items-end">
              <div><label className="block text-[10px] font-semibold mb-1">Gen. (mm)</label><input type="number" value={labelW} onChange={(e) => setLabelW(Number(e.target.value))} className="zen-input !py-1.5 !text-xs" /></div>
              <div><label className="block text-[10px] font-semibold mb-1">Yük. (mm)</label><input type="number" value={labelH} onChange={(e) => setLabelH(Number(e.target.value))} className="zen-input !py-1.5 !text-xs" /></div>
              <label className="flex items-center gap-1 text-[10px] font-semibold cursor-pointer">
                <input type="checkbox" checked={autoPrintLabel} onChange={(e) => setAutoPrintLabel(e.target.checked)} className="rounded" />
                Direkt Yazdır
              </label>
              <button data-testid="print-label" onClick={() => generateLabelPDF(order, { w: labelW, h: labelH, autoprint: autoPrintLabel })} className="zen-btn-primary !text-xs">Etiket Al</button>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={() => generateLabelPDF(order, { w: 50, h: 25, autoprint: autoPrintLabel })} className="zen-btn-ghost !text-[10px] !py-1 !px-2">50×25</button>
              <button onClick={() => generateLabelPDF(order, { w: 70, h: 40, autoprint: autoPrintLabel })} className="zen-btn-ghost !text-[10px] !py-1 !px-2">70×40</button>
              <button onClick={() => generateLabelPDF(order, { w: 100, h: 50, autoprint: autoPrintLabel })} className="zen-btn-ghost !text-[10px] !py-1 !px-2">100×50</button>
              <button onClick={() => generateLabelPDF(order, { w: 100, h: 75, autoprint: autoPrintLabel })} className="zen-btn-ghost !text-[10px] !py-1 !px-2">100×75</button>
              {(order.items || []).some(i => (i.product + (i.groupSlug||"")).toLowerCase().includes("zip") || (i.product + (i.groupSlug||"")).toLowerCase().includes("sky")) && (
                <button
                  onClick={() => setShowItemLabels(true)}
                  data-testid="print-item-labels-btn"
                  className="zen-btn-primary !text-[10px] !py-1 !px-2 ml-auto"
                >
                  <Tag className="w-3 h-3" /> Zip Kalem Etiketleri
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => smartSharePDF(order, "whatsapp")} data-testid="share-whatsapp-pdf" className="flex items-center gap-3 p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 transition">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-emerald-500 text-white flex items-center justify-center"><Send className="w-5 h-5" /></div>
              <div className="text-left flex-1"><div className="font-bold text-sm text-emerald-600">WhatsApp</div><div className="text-[10px] text-slate-500">Teklif PDF + özet gönder</div></div>
            </button>
            <button onClick={() => smartSharePDF(order, "email")} data-testid="share-email-pdf" className="flex items-center gap-3 p-4 rounded-lg border border-sky-500/30 bg-sky-500/5 hover:bg-sky-500/10 transition">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-sky-500 text-white flex items-center justify-center"><Mail className="w-5 h-5" /></div>
              <div className="text-left flex-1"><div className="font-bold text-sm text-sky-600">E-Posta</div><div className="text-[10px] text-slate-500">Teklif PDF + özet gönder</div></div>
            </button>
          </div>
          <div className="text-[10px] text-slate-400 px-1 flex items-center gap-1"><Share2 className="w-3 h-3" /> Mobilde PDF doğrudan paylaşılır; bilgisayarda PDF indirilir + özet metni açılır.</div>
        </div>
      </div>
    </div>
  );
}
